import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Video, VideoOff, Mic, MicOff, PhoneOff, Circle,
  StopCircle, Download, Loader2, Monitor, Users,
  AlertCircle, CheckCircle2
} from 'lucide-react';

// Google STUN servers — free, no account needed
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const POLL_INTERVAL_MS = 1500;

export default function VideoInterviewPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();

  // Room state
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  // Media
  const [camOn, setCamOn]   = useState(true);
  const [micOn, setMicOn]   = useState(true);
  const [inCall, setInCall] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Recording
  const [recording, setRecording]     = useState(false);
  const [recordings, setRecordings]   = useState([]);
  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);

  // Refs
  const localVideoRef  = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const pcRef          = useRef(null);
  const pollRef        = useRef(null);
  const pendingCandidatesRef = useRef([]);

  useEffect(() => {
    loadRoom();
    return () => { stopPolling(); cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const loadRoom = async () => {
    try {
      const res = await api.get(`/video/rooms/${roomId}`);
      setRoom(res.data);
      // Load existing recordings
      const recRes = await api.get(`/video/rooms/${roomId}/recordings`);
      setRecordings(recRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Room not found or unauthorized');
    } finally {
      setLoading(false);
    }
  };

  /* ── Media Setup ── */
  const getLocalStream = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: camOn, audio: micOn });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      return stream;
    } catch (e) {
      toast.error('Camera/mic access denied. Check browser permissions.');
      throw e;
    }
  }, [camOn, micOn]);

  const toggleCam = () => {
    const enabled = !camOn;
    setCamOn(enabled);
    localStreamRef.current?.getVideoTracks().forEach(t => { t.enabled = enabled; });
  };

  const toggleMic = () => {
    const enabled = !micOn;
    setMicOn(enabled);
    localStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = enabled; });
  };

  /* ── WebRTC ── */
  const createPC = useCallback(() => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = async ({ candidate }) => {
      if (candidate) {
        await api.post(`/video/rooms/${roomId}/signal`, {
          type: 'ice',
          data: candidate.toJSON(),
        }).catch(() => {});
      }
    };

    pc.ontrack = ({ streams }) => {
      if (remoteVideoRef.current && streams[0]) {
        remoteVideoRef.current.srcObject = streams[0];
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        setConnecting(false);
        setInCall(true);
        toast.success('Connected!');
      }
      if (['disconnected', 'failed', 'closed'].includes(pc.connectionState)) {
        setInCall(false);
        toast.info('Call ended');
      }
    };

    pcRef.current = pc;
    return pc;
  }, [roomId, api]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/video/rooms/${roomId}/signals`);
        for (const sig of res.data) {
          await handleIncomingSignal(sig); // eslint-disable-line react-hooks/exhaustive-deps
        }
      } catch {}
    }, POLL_INTERVAL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, api]);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const handleIncomingSignal = async (sig) => {
    const pc = pcRef.current;
    if (!pc) return;
    if (sig.type === 'offer') {
      await pc.setRemoteDescription(new RTCSessionDescription(sig.data));
      // Flush any queued ICE candidates
      for (const c of pendingCandidatesRef.current) {
        await pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
      }
      pendingCandidatesRef.current = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await api.post(`/video/rooms/${roomId}/signal`, { type: 'answer', data: answer });
    } else if (sig.type === 'answer') {
      await pc.setRemoteDescription(new RTCSessionDescription(sig.data)).catch(() => {});
    } else if (sig.type === 'ice') {
      if (pc.remoteDescription) {
        await pc.addIceCandidate(new RTCIceCandidate(sig.data)).catch(() => {});
      } else {
        pendingCandidatesRef.current.push(sig.data);
      }
    }
  };

  const startCall = async () => {
    setConnecting(true);
    try {
      const stream = await getLocalStream();
      const pc = createPC();
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      startPolling();

      // Only the employer (host) creates the offer
      if (user?.user_type === 'employer') {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await api.post(`/video/rooms/${roomId}/signal`, { type: 'offer', data: offer });
      }
    } catch (e) {
      setConnecting(false);
      toast.error('Failed to start call');
    }
  };

  const endCall = () => {
    cleanup();
    setInCall(false);
    setConnecting(false);
    stopPolling();
    toast.info('Call ended');
  };

  const cleanup = () => {
    pcRef.current?.close();
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    if (localVideoRef.current)  localVideoRef.current.srcObject  = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
  };

  /* ── Recording ── */
  const startRecording = () => {
    const stream = localStreamRef.current;
    if (!stream) { toast.error('Start the call first'); return; }
    chunksRef.current = [];
    const mr = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus' });
    mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = uploadRecording;
    mr.start(1000);
    mediaRecorderRef.current = mr;
    setRecording(true);
    toast.info('Recording started');
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const uploadRecording = async () => {
    const blob = new Blob(chunksRef.current, { type: 'video/webm' });
    const fd = new FormData();
    fd.append('file', blob, `interview-${Date.now()}.webm`);
    try {
      const res = await api.post(`/video/rooms/${roomId}/recording`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setRecordings(prev => [...prev, res.data]);
      toast.success('Recording saved');
    } catch {
      toast.error('Failed to upload recording');
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-white animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Room Unavailable</h2>
        <p className="text-gray-400 mb-4">{error}</p>
        <Button onClick={() => navigate(-1)} variant="outline" className="text-white border-white">Go Back</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <Video className="w-5 h-5 text-[#E11D48]" />
          <div>
            <p className="font-semibold text-sm">Video Interview</p>
            <p className="text-xs text-gray-400">Room ID: {roomId?.slice(0, 8)}…</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {inCall && (
            <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-1.5 animate-pulse inline-block" />
              Live
            </Badge>
          )}
          {recording && (
            <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
              <span className="w-2 h-2 bg-red-400 rounded-full mr-1.5 animate-pulse inline-block" />
              Recording
            </Badge>
          )}
        </div>
      </div>

      {/* Video Grid */}
      <div className="flex-1 flex flex-col md:flex-row gap-4 p-4">
        {/* Remote video (main) */}
        <div className="flex-1 bg-gray-800 rounded-2xl overflow-hidden relative flex items-center justify-center min-h-64">
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
          {!inCall && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
              <Users className="w-16 h-16 mb-3 opacity-30" />
              <p className="text-sm">{connecting ? 'Connecting…' : 'Waiting for other participant'}</p>
            </div>
          )}
          <div className="absolute bottom-3 left-3 text-xs text-gray-300 bg-black/50 px-2 py-1 rounded">
            Remote
          </div>
        </div>

        {/* Local video (pip) */}
        <div className="w-full md:w-56 bg-gray-800 rounded-2xl overflow-hidden relative flex items-center justify-center min-h-40">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          {!camOn && (
            <div className="absolute inset-0 flex items-center justify-center">
              <VideoOff className="w-8 h-8 text-gray-500" />
            </div>
          )}
          <div className="absolute bottom-2 left-2 text-xs text-gray-300 bg-black/50 px-2 py-1 rounded">
            You
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 py-4 px-6 bg-gray-800 border-t border-gray-700">
        <button
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${micOn ? 'bg-gray-600 hover:bg-gray-500' : 'bg-red-500 hover:bg-red-600'}`}
          onClick={toggleMic}
          title={micOn ? 'Mute' : 'Unmute'}
        >
          {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>

        <button
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${camOn ? 'bg-gray-600 hover:bg-gray-500' : 'bg-red-500 hover:bg-red-600'}`}
          onClick={toggleCam}
          title={camOn ? 'Camera off' : 'Camera on'}
        >
          {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </button>

        {!inCall && !connecting ? (
          <button
            className="px-6 h-12 rounded-full bg-green-500 hover:bg-green-600 flex items-center gap-2 font-semibold transition-colors"
            onClick={startCall}
          >
            <Video className="w-5 h-5" /> Join Call
          </button>
        ) : connecting ? (
          <button className="px-6 h-12 rounded-full bg-green-500/50 flex items-center gap-2 font-semibold cursor-not-allowed" disabled>
            <Loader2 className="w-5 h-5 animate-spin" /> Connecting…
          </button>
        ) : (
          <button
            className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors"
            onClick={endCall}
            title="End call"
          >
            <PhoneOff className="w-6 h-6" />
          </button>
        )}

        {/* Recording controls */}
        {inCall && (
          <button
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${recording ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-600 hover:bg-gray-500'}`}
            onClick={recording ? stopRecording : startRecording}
            title={recording ? 'Stop recording' : 'Start recording'}
          >
            {recording ? <StopCircle className="w-5 h-5" /> : <Circle className="w-5 h-5 text-red-400" />}
          </button>
        )}
      </div>

      {/* Recordings list */}
      {recordings.length > 0 && (
        <div className="px-6 py-4 bg-gray-800 border-t border-gray-700">
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Saved Recordings</p>
          <div className="flex flex-wrap gap-2">
            {recordings.map((rec, i) => (
              <a
                key={rec.id}
                href={`${process.env.REACT_APP_BACKEND_URL}${rec.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Recording {i + 1}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
